import spartaSingletonUtility from '@sparta.global.utilities/sparta-singleton-utility/dist/es6.js';
import spartaTransportUtility from '@sparta.global.utilities/sparta-transport-utility/dist/es6.js';
import benchmarkUtility from '@sparta.common.utilities/benchmark-utility/dist/es6.js';

/**
 * @name        Rates Utility
 * @file        rates-utility.js
 * @description Requests rates from homepageweb service.
 * @classdesc   Requests rates from homepageweb service.
 * @global      Creates a global RatesUtility object
 * @author      zkff3d3
 * @copyright   © 2017 Bank of America Corporation. All rights reserved.
 */

/**
 * Rates Utility class
 */
class RatesUtility {
  /**
   * Creates an instance of RatesUtility.
   */
  constructor() {
    this.Transport = spartaTransportUtility;
    if (spaParams.siteParams.jsTimer) {
      benchmarkUtility.init(this);
    }
  }

  /**
   * Request rates.
   *
   * @mockSpaParams {
   *   siteParams: {
   *     ratesEndpoint: 'http://localhost:8080/rates',
   *     defaultState: 'NC',
   *     defaultZipCode: '28202',
   *   }
   * }
   * @mockAjax http://localhost:8080/rates?state=NC&zipcode=28202 {
   *  "refinanceRates": [{
   *    "apr": 1.22345,
   *    "rate": 5.667788,
   *    "points": 14.56565656,
   *    "effectiveDate": "Tuesday, November 27, 2018 2:01:20 PM EST"
   *  }],
   *  "purchaseRates": [{
   *    "apr": 1.22345,
   *    "rate": 5.667788,
   *    "points": 14.56565656,
   *    "effectiveDate": "Tuesday, November 27, 2018 2:01:20 PM EST"
   *  }]
   * }
   * @testExample request()
   * @testOutput deepEqual {
   *  "refinanceRates": [{
   *    "apr": "1.223",
   *    "rate": "5.668",
   *    "points": "14.566",
   *    "effectiveDate": "Tuesday, November 27, 2018 2:01:20 PM EST"
   *  }],
   *  "purchaseRates": [{
   *    "apr": "1.223",
   *    "rate": "5.668",
   *    "points": "14.566",
   *    "effectiveDate": "Tuesday, November 27, 2018 2:01:20 PM EST"
   *  }],
   *  "refinanceRatesEffectiveDate": "11/27/2018",
   *  "refinanceRatesEffectiveDateTime": "11/27/2018 2:01 PM ET",
   *  "refinanceRatesEffectiveTime": "2:01 PM ET",
   *  "purchaseRatesEffectiveDate": "11/27/2018",
   *  "purchaseRatesEffectiveDateTime": "11/27/2018 2:01 PM ET",
   *  "purchaseRatesEffectiveTime": "2:01 PM ET"
   * }
   * @returns {Promise} Promise containing response from endpoint
   */
  async request() {
    const {
      siteParams
    } = spaParams;
    const state = siteParams.defaultState;
    const zipCode = siteParams.defaultZipCode;
    const url = `${siteParams.ratesEndpoint}?state=${state}&zipcode=${zipCode}`;
    return new Promise((resolve, reject) => {
      const obj = {
        url,
        method: 'GET',
        contentType: 'application/json',
        dataType: 'json',
        error: reject,
        success: response => {
          RatesUtility.normalize(response);
          resolve(response);
        },
        cache: true
      };
      this.Transport.subscribe(obj);
    });
  }

  /**
   * Mutates the response object - normalizing the data.
   *
   * @static
   * @param {Object} response object to be normalized
   * @returns {void}
   */
  static normalize(response) {
    let effectiveDate = '';
    Object.keys(response).forEach(idx => {
      if (!(response[idx] instanceof Array)) return; // short-circuit if no rates

      if (['refinanceRates', 'purchaseRates'].indexOf(idx) >= 0) {
        response[idx].forEach(rate => {
          Object.assign(rate, {
            apr: rate.apr.toFixed(3),
            rate: rate.rate.toFixed(3),
            points: rate.points.toFixed(3)
          });
          ({
            effectiveDate
          } = rate);
        });

        // Mortgage rates need to display the first 30 years first etc. Just reversing the order.
        // Can eventually sort based on the whitelist when we can map that to a specific key.
        response[idx].reverse();
      } else if (idx === 'helocRates' && response[idx]) {
        response[idx].forEach(rate => {
          ({
            effectiveDate
          } = rate);
        });
      } else if (idx === 'autoLoanRates') {
        response[idx].forEach(rate => {
          effectiveDate = rate.timeStamp;

          // eslint-disable-next-line no-param-reassign
          rate.ratesEffectivePercentage = RatesUtility.reverseObject(rate.ratesEffectivePercentage);
        });
      }
      response[`${idx}EffectiveDate`] = RatesUtility.formatDate(effectiveDate);
      response[`${idx}EffectiveTime`] = RatesUtility.formatTime(effectiveDate);
      response[`${idx}EffectiveDateTime`] = RatesUtility.formatDateTime(effectiveDate);
    });
  }

  /**
   * Format datestring to m/d/yyyy h:i a format
   *
   * @static
   * @param {String} dateString to be formatted
   * @returns {String} formatted dateTimeString
   */
  static formatDateTime(dateString) {
    const displayDate = RatesUtility.formatDate(dateString);
    const displayTime = RatesUtility.formatTime(dateString);
    return `${displayDate} ${displayTime}`;
  }

  /**
   * Format datestring to m/d/yyyy format
   *
   * @static
   * @param {String} dateString to be formatted
   * @returns {String} formatted dateString
   */
  static formatDate(dateString) {
    const date = new Date(dateString);
    const displayDate = date.toLocaleDateString();
    return displayDate;
  }

  /**
   * Format datestring to h:i a format
   *
   * @static
   * @param {String} dateString to be formatted
   * @returns {String} formatted timeString
   */
  static formatTime(dateString) {
    const date = new Date(dateString);
    let displayTime = date.toLocaleTimeString();
    const timeSegments = displayTime.split(':');
    const lastSegment = timeSegments.pop().split(' ').pop();
    displayTime = `${timeSegments.join(':')} ${lastSegment} ET`;
    return displayTime;
  }

  /**
   * Reverse objects
   * @param {Object} obj the object to reverse
   * @returns {Object} the reversed object
   */
  static reverseObject(obj) {
    const sorted = {};
    const arr = [];
    Object.keys(obj).forEach(key => {
      arr.push(key);
    });
    arr.reverse();
    for (let k = 0; k < arr.length; k += 1) {
      sorted[arr[k]] = obj[arr[k]];
    }
    return sorted;
  }
}

/**
 * @const ratesUtility
 * @description single instance of RatesUtility used to expose this utility
 */
const ratesUtility = spartaSingletonUtility('sparta.utility.ratesUtility', RatesUtility);

export { ratesUtility as default };
